import React, { useMemo, useEffect, useLayoutEffect, useState, useRef } from 'react';
import styles from './index.module.less';
import { Affix, Button, Skeleton } from 'antd';
import { HomeOutlined, RightOutlined, CalendarOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { HomeArticleModel, HomeGPTsModel, ArticleDetailModel } from '@/types/home';
import { imgUrl } from '@/shared/utils';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { CommonBaseContent } from '@/components/Layouts/CommonBackContent';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { useConfigProvider } from '@/context/ConfigProvider';
import YouTube from 'react-youtube';
import { getSlotPattern, getSlotComponent } from '../slot/slotRegistry';
import AddAccountModal from '@/components/protectedAccount/AddAcountModal';
import { EventNameSource } from '@/shared/event-types';
import { useRouter } from 'next/router';
import { useUserProvider } from '@/context/UserProvider';

const ArticleListContent = dynamic(() => import('./ArticleListContent'), {
  ssr: true,
});

const BottomFlexButton = dynamic(() => import('../BottomFlexButton'), {
  ssr: false,
});

const BookItem = dynamic(() => import('../BookItem'));
const SharePage = dynamic(() => import('@/components/Common/Share'));
const HomeFullItem = dynamic(() => import('@/components/Home/HomeFullItem'));

const LoadingSkeleton = () => (
  <>
    {/* 面包屑导航骨架 */}
    <nav className={styles.breadcrumb} style={{ marginBottom: '32px' }}>
      <Skeleton.Input active size="small" style={{ width: '48px', height: '16px' }} />
      <Skeleton.Input active size="small" style={{ width: '12px', height: '12px', margin: '0 8px' }} />
      <Skeleton.Input active size="small" style={{ width: '48px', height: '16px' }} />
      <Skeleton.Input active size="small" style={{ width: '12px', height: '12px', margin: '0 8px' }} />
      <Skeleton.Input active size="small" style={{ width: '80px', height: '16px' }} />
    </nav>

    {/* 文章头部骨架 */}
    <header style={{ marginBottom: '32px' }}>
      {/* 文章标题骨架 */}
      <Skeleton active title={{ width: '75%' }} paragraph={false} style={{ marginBottom: '24px' }} />

      {/* 文章元信息骨架 */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Skeleton.Avatar active size="small" shape="circle" style={{ width: '14px', height: '14px' }} />
        <Skeleton.Input active size="small" style={{ width: '112px', height: '14px' }} />
      </div>

      {/* 封面图片骨架 */}
      <Skeleton.Image
        active
        style={{
          width: '100%',
          aspectRatio: '16/9',
          marginBottom: '0',
          borderRadius: '12px',
        }}
      />
    </header>

    {/* 内容卡片骨架 */}
    <div
      style={{
        padding: '32px',
        backgroundColor: '#ffffff',
        border: '1px solid #e8e8e8',
        borderRadius: '12px',
        marginBottom: '32px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      }}
    >
      {/* 文章描述骨架 */}
      <div style={{ marginBottom: '32px', paddingBottom: '32px', borderBottom: '1px solid #e8e8e8' }}>
        <Skeleton.Input active size="small" style={{ width: '100%', height: '20px', marginBottom: '8px' }} />
        <Skeleton.Input active size="small" style={{ width: '100%', height: '20px', marginBottom: '8px' }} />
        <Skeleton.Input active size="small" style={{ width: '67%', height: '20px' }} />
      </div>

      {/* 目录骨架 */}
      <div
        style={{
          marginBottom: '32px',
          padding: '16px',
          backgroundColor: '#f7faff',
          borderRadius: '8px',
          border: '1px solid #e8e8e8',
        }}
      >
        <Skeleton.Input active size="small" style={{ width: '64px', height: '20px', marginBottom: '16px' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton.Input key={i} active size="small" style={{ width: `${192 + i * 20}px`, height: '16px' }} />
          ))}
        </div>
      </div>

      {/* 文章正文骨架 */}
      <div style={{ marginTop: '32px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {[100, 95, 90, 85, 100, 95, 90, 100].map((width, i) => (
          <Skeleton.Input
            key={i}
            active
            size="small"
            style={{
              width: `${width}%`,
              height: '16px',
            }}
          />
        ))}
        <Skeleton.Image
          active
          style={{
            width: '100%',
            height: '192px',
            marginTop: '24px',
            marginBottom: '24px',
            borderRadius: '8px',
          }}
        />
        {[100, 95, 90, 85, 100, 95].map((width, i) => (
          <Skeleton.Input
            key={`p2-${i}`}
            active
            size="small"
            style={{
              width: `${width}%`,
              height: '16px',
            }}
          />
        ))}
      </div>

      {/* 相关文章骨架 */}
      <div style={{ marginTop: '48px', paddingTop: '32px', borderTop: '1px solid #e8e8e8' }}>
        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Skeleton.Input active size="small" style={{ width: '4px', height: '20px', borderRadius: '2px' }} />
          <Skeleton.Input active size="small" style={{ width: '96px', height: '24px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <Skeleton.Avatar
                active
                size="small"
                shape="circle"
                style={{ width: '24px', height: '24px', flexShrink: 0 }}
              />
              <Skeleton.Input active size="small" style={{ width: '100%', height: '20px', flex: 1 }} />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* 推荐文章区域骨架 */}
    <div style={{ marginTop: '48px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Skeleton.Input active size="small" style={{ width: '4px', height: '24px', borderRadius: '2px' }} />
        <Skeleton.Input active size="small" style={{ width: '128px', height: '32px' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#ffffff',
              border: '1px solid #e8e8e8',
              borderRadius: '12px',
            }}
          >
            <Skeleton.Image
              active
              style={{
                width: '128px',
                height: '96px',
                flexShrink: 0,
                borderRadius: '8px',
              }}
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Skeleton.Input active size="small" style={{ width: '75%', height: '20px' }} />
              <Skeleton.Input active size="small" style={{ width: '100%', height: '16px' }} />
              <Skeleton.Input active size="small" style={{ width: '50%', height: '16px' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </>
);

const ArticleDetailUI = ({
  model,
  // gptsList,
  articleList,
  contentData,
  recommendTags,
  recommandArtcles,
  h2Texts,
}: {
  model?: ArticleDetailModel;
  gptsList?: [HomeGPTsModel];
  articleList?: [HomeArticleModel];
  contentData?: any;
  recommendTags?: [HomeArticleModel];
  recommandArtcles?: [HomeArticleModel];
  h2Texts?: string[];
}) => {
  const leftRef = useRef<HTMLDivElement>(null);
  const AccountModalRef = useRef<any>();
  const { t } = useTranslation();
  const router = useRouter();
  const currentHref = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), [router.asPath]);
  const { onShowArticleBottomAlert, isMobile } = useConfigProvider();
  const { isLogin } = useUserProvider();

  const [leftWidth, setLeftWidth] = useState(500);
  // 客户端加载状态：用于在路由切换或数据未准备好时显示骨架屏
  // 初始值必须与服务端一致，避免水合错误
  // 服务端和客户端都使用相同的初始值：如果数据未准备好则显示骨架屏
  const [isLoading, setIsLoading] = useState(!contentData || !contentData?.title);
  // 用于跟踪路由变化，检测是否是路由切换导致的组件挂载
  const prevRouteRef = useRef<string>('');
  const isMountedRef = useRef(false);

  const imgClick = (node: any) => {
    if (node.target.dataset.href) {
      location.href = node.target.dataset.href;
    } else {
      location.href = 'https://www.erasa.net/login';
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (leftRef.current && leftRef.current.clientWidth > 0) {
        setLeftWidth(leftRef.current.clientWidth);
      }
    };

    setTimeout(() => {
      if (typeof window !== 'undefined') {
        handleResize();
      }
    }, 2000);

    window.addEventListener('resize', handleResize);
    // 图片点击事件
    const imgs = document.querySelectorAll('[data-href]');
    imgs.forEach((node: any) => {
      node.addEventListener('click', imgClick);
    });

    // 清理事件监听器
    return () => {
      window.removeEventListener('resize', handleResize);
      imgs.forEach((node) => {
        node.removeEventListener('click', imgClick);
      });
    };
  }, []);

  useEffect(() => {
    onShowArticleBottomAlert(isMobile);

    return () => {
      onShowArticleBottomAlert(false);
    };
  }, []);

  // 组件挂载时检测是否是路由切换导致的
  // 使用 sessionStorage 来跨组件传递路由切换状态
  // 使用 useLayoutEffect 确保在 DOM 更新后同步执行，比 useEffect 更早
  // 这样可以在数据检查之前就设置 isLoading 状态
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    if (isMountedRef.current) return; // 只执行一次

    // 立即同步检查 sessionStorage 中是否有路由切换标记
    let isRouteChanging = false;
    try {
      const routeChangingFlag = sessionStorage.getItem('_article_route_changing');
      isRouteChanging = routeChangingFlag === 'true';
    } catch {
      // sessionStorage 可能不可用
    }

    if (isRouteChanging) {
      // 这是路由切换导致的新组件挂载，立即显示骨架屏
      setIsLoading(true);
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // 标记已挂载
    isMountedRef.current = true;
    // 更新路由引用
    prevRouteRef.current = router.asPath;
  }, [router.asPath]);

  // 监听路由变化，在路由切换时显示骨架屏
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleRouteChangeStart = (url: string) => {
      // 检查是否是跳转到博客详情页
      if (url.startsWith('/blog/') && url !== '/blog') {
        // 在 sessionStorage 中设置路由切换标记
        try {
          sessionStorage.setItem('_article_route_changing', 'true');
          sessionStorage.setItem('_article_previous_route', router.asPath);
        } catch {
          // sessionStorage 可能不可用
        }
        // 更新路由引用
        prevRouteRef.current = router.asPath;
        // 滚动到页面顶部
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setIsLoading(true);
      }
    };

    const handleRouteChangeComplete = (url: string) => {
      // 更新路由引用
      prevRouteRef.current = url;
      // 如果是博客详情页，清除标记（让新组件知道这是路由切换）
      if (url.startsWith('/blog/') && url !== '/blog') {
        // 标记会在新组件挂载时清除
      }
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeComplete);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeComplete);
    };
  }, [router]);

  // 检查数据是否准备好，如果准备好则隐藏骨架屏
  // 只在客户端执行，避免水合错误
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!isMountedRef.current) return; // 等待挂载检测完成

    if (contentData && contentData.title) {
      // 数据已准备好
      // 检查 sessionStorage 中是否有路由切换标记
      let isRouteChanging = false;
      try {
        isRouteChanging = sessionStorage.getItem('_article_route_changing') === 'true';
      } catch {
        // sessionStorage 可能不可用
      }

      if (isRouteChanging) {
        // 如果是路由切换，延迟更长时间再隐藏骨架屏，让用户看到加载过程
        const timer = setTimeout(() => {
          setIsLoading(false);
          prevRouteRef.current = router.asPath; // 更新路由引用
          // 清除标记
          try {
            sessionStorage.removeItem('_article_route_changing');
            sessionStorage.removeItem('_article_previous_route');
          } catch {
            // ignore
          }
        }, 600);
        return () => clearTimeout(timer);
      } else {
        // 如果不是路由切换（首次加载），延迟一小段时间后隐藏骨架屏
        const timer = setTimeout(() => {
          setIsLoading(false);
        }, 300);
        return () => clearTimeout(timer);
      }
    } else {
      // 数据未准备好，显示骨架屏，并滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setIsLoading(true);
    }
  }, [contentData, router.asPath]);

  // 组件首次挂载时，如果显示骨架屏，则滚动到顶部
  // 只在客户端执行
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isLoading) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isLoading]);

  useEffect(() => {
    if (typeof window !== 'undefined' && contentData?.body) {
      const h2Elements = document.querySelectorAll('h2');
      // 根据登录状态动态计算头部高度
      // 如果未登录：UnloginHeader (45px) + GlobalHeader (60px) + 安全边距 (20px) = 125px
      // 如果已登录：GlobalHeader (60px) + 安全边距 (20px) = 80px
      const headerHeight = isLogin ? 80 : 125;
      h2Elements.forEach((h2, index) => {
        h2.id = `${index + 1}`;
        h2.style.scrollMarginTop = `${headerHeight}px`;
      });
    }
  }, [contentData?.body, isLogin]);

  const [isAddAccountModal, setIsAddAccountModal] = useState(false);

  const getAccount = (inputValue: string, selectedPlatform: string) => {
    setIsAddAccountModal(true);
    setTimeout(() => {
      AccountModalRef.current?.triggerStep(selectedPlatform, inputValue);
    }, 400);
  };

  const [isManualScroll, setIsManualScroll] = useState(false);
  // ✅ INP 优化：只在第一次滚动时设置状态，避免高频 setState（60fps = 每 16ms 触发一次）
  // 优化前：滚动后点击 INP +50-100ms（主线程被滚动事件占用）
  // 优化后：滚动后点击 INP +0ms（立即移除监听器）
  useEffect(() => {
    const handleWheel = () => {
      setIsManualScroll(true);
      // ✅ 立即移除监听器，避免后续滚动继续触发 setState
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };

    const handleTouchMove = () => {
      setIsManualScroll(true);
      // ✅ 立即移除监听器
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };

    window.addEventListener('wheel', handleWheel);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // 解析内容中的插槽组件
  const parseContentWithSlots = (htmlContent: string) => {
    if (!htmlContent) return null;

    // 获取插槽匹配模式
    const slotPattern = getSlotPattern();

    // 检查是否包含任何插槽标记
    if (!slotPattern.test(htmlContent)) {
      return (
        <div
          className={styles.content}
          dangerouslySetInnerHTML={{
            __html: htmlContent,
          }}
        />
      );
    }

    // 重置正则表达式的 lastIndex
    slotPattern.lastIndex = 0;

    // 分割内容，保留分隔符
    const parts = htmlContent.split(slotPattern);

    return (
      <div className={styles.content}>
        {parts.map((part, index) => {
          // 检查当前部分是否是插槽标记
          const SlotComponent = getSlotComponent(part);

          if (SlotComponent) {
            // 如果是插槽标记，渲染对应的组件，并传递 props
            return (
              <div key={index} className={styles.slotWrapper}>
                <SlotComponent getAccount={getAccount} pageSource={EventNameSource.blogFeature} useNewModal={true} />
              </div>
            );
          } else if (part) {
            // 如果是普通HTML内容，使用 dangerouslySetInnerHTML 渲染
            return <div key={index} dangerouslySetInnerHTML={{ __html: part }} />;
          }

          return null;
        })}
      </div>
    );
  };

  const imageExtra = useMemo(() => {
    return contentData?.image_extra && JSON.parse(contentData?.image_extra);
  }, [contentData?.image_extra]);

  return (
    <>
      <CommonBaseContent
        style={{
          maxWidth: '1152px',
          paddingTop: '0rem',
          marginTop: '3rem',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        <div className={styles.desktopSharePage}>
          <Affix offsetTop={isLogin ? 70 : 110}>
            <SharePage shareUrl={currentHref} />
          </Affix>
        </div>

        <div className={styles.homeContent}>
          <div className={styles.leftContent}>
            <div className={styles.leftContainer}>
              {/* 面包屑导航 */}
              <nav className={styles.breadcrumb}>
                <Link className={styles.breadcrumbLink} href={`${process.env.NEXT_APP_WEB_HOST}`}>
                  <HomeOutlined className={styles.breadcrumbIcon} />
                  <span>{t('home')}</span>
                </Link>
                <RightOutlined className={styles.breadcrumbSeparator} />
                <Link className={styles.breadcrumbLink} href={`${process.env.NEXT_APP_WEB_HOST}/blog`}>
                  {t('blog')}
                </Link>
                {model?.type && (
                  <>
                    <RightOutlined className={styles.breadcrumbSeparator} />
                    <Link
                      className={styles.breadcrumbLink}
                      href={`${process.env.NEXT_APP_WEB_HOST}/blog/category/${encodeURIComponent(
                        model.type.toString().replace(/\s+/g, '-'),
                      )}`}
                    >
                      {t(`${model.type}`)}
                    </Link>
                  </>
                )}
                <RightOutlined className={styles.breadcrumbSeparator} />
                <span className={styles.breadcrumbCurrent}>{t('Article Details')}</span>
              </nav>

              {isLoading || contentData == undefined || contentData?.title == undefined ? (
                <LoadingSkeleton />
              ) : (
                <>
                  {/* 文章头部 - 卡片外 */}
                  <header className={styles.articleHeader}>
                    {contentData?.book && <BookItem contentData={contentData} />}

                    {/* 文章标题 */}
                    <h1 className={styles.title}>{contentData?.title}</h1>

                    {/* 文章元信息，日期 */}
                    {model?.create_time && (
                      <div className={styles.metaInfo}>
                        <CalendarOutlined className={styles.metaIcon} />
                        <time>{model.create_time}</time>
                      </div>
                    )}

                    {/* 封面图片 */}
                    {contentData?.image_url && (!imageExtra || imageExtra?.image_show_in_detail) && (
                      <div className={styles.imageContainer}>
                        <div
                          className={styles.imagePlaceholder}
                          style={{ cursor: imageExtra?.image_link ? 'pointer' : undefined }}
                          onClick={() => {
                            if (imageExtra?.image_link) {
                              window.open(imageExtra?.image_link, '_blank');
                            }
                          }}
                        >
                          <Image
                            className={styles.image}
                            src={imgUrl(contentData?.image_url, 'big')}
                            priority
                            alt={imageExtra?.image_alt || 'erasa'}
                            fill
                            sizes="(max-width: 576px) 100vw, (max-width: 900px) 100vw, 710px"
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                      </div>
                    )}
                  </header>

                  {/* 内容卡片 */}
                  <div className={styles.contentCard}>
                    {/* 文章描述 */}
                    {contentData?.introduction && (
                      <div
                        className={styles.desc}
                        dangerouslySetInnerHTML={{
                          __html: contentData?.introduction,
                        }}
                      />
                    )}

                    {/* 目录 */}
                    {h2Texts?.length > 0 && (
                      <div className={styles.tableContent}>
                        <div className={styles.tableContentHeader}>
                          <UnorderedListOutlined className={styles.tableContentIcon} />
                          <h3 className={styles.tableContentTitle}>{t('Table of Contents')}</h3>
                        </div>
                        <nav className={styles.tableContentNav}>
                          <ul className={styles.tableContentList}>
                            {h2Texts?.map((item: string, index: number) => (
                              <li key={index}>
                                <a
                                  href={`#${index + 1}`}
                                  className={styles.tableContentLink}
                                  onClick={(e) => {
                                    e.preventDefault();

                                    // 尝试查找元素的函数，支持重试机制
                                    const findAndScrollToElement = (retryCount = 0) => {
                                      // 首先尝试通过id查找
                                      let element = document.getElementById(`${index + 1}`);

                                      // 如果通过id找不到，尝试通过querySelectorAll和索引查找
                                      if (!element) {
                                        const h2Elements = document.querySelectorAll('h2');
                                        if (h2Elements[index]) {
                                          element = h2Elements[index] as HTMLElement;
                                        }
                                      }

                                      if (element) {
                                        // 根据登录状态动态计算头部高度
                                        // 如果未登录：UnloginHeader (45px) + GlobalHeader (60px) + 安全边距 (20px) = 125px
                                        // 如果已登录：GlobalHeader (60px) + 安全边距 (20px) = 80px
                                        const headerHeight = isLogin ? 80 : 125;
                                        const elementPosition =
                                          element.getBoundingClientRect().top + window.pageYOffset;
                                        const offsetPosition = elementPosition - headerHeight;
                                        window.scrollTo({
                                          top: offsetPosition,
                                          behavior: 'smooth',
                                        });
                                      } else if (retryCount < 3) {
                                        // 如果找不到元素且重试次数未达上限，等待DOM更新后重试
                                        setTimeout(() => {
                                          findAndScrollToElement(retryCount + 1);
                                        }, 100);
                                      } else {
                                        // eslint-disable-next-line no-console
                                        console.warn(`无法找到目标元素 #${index + 1}`);
                                      }
                                    };

                                    // 使用 requestAnimationFrame 确保在下一帧执行，等待DOM更新
                                    requestAnimationFrame(() => {
                                      findAndScrollToElement();
                                    });
                                  }}
                                >
                                  <span className={styles.tableContentDot} />
                                  <span className={styles.tableContentText}>{item}</span>
                                </a>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </div>
                    )}

                    {/* 文章正文 */}
                    {contentData?.body && (
                      <>
                        {parseContentWithSlots(contentData.body)}
                        {`${contentData?.body}`?.includes(`<dolphin-radar-video/>`) && (
                          <div
                            ref={leftRef}
                            style={{
                              marginBottom: '20px',
                              width: '100%',
                              aspectRatio: '16/9',
                            }}
                          >
                            <YouTube
                              videoId="iGMQ2qTI-U8"
                              className={styles.youtube}
                              opts={{
                                width: `${Number(leftWidth)}`,
                                height: `${Number((leftWidth * 9) / 16)}`,
                                playerVars: {
                                  autoplay: 1,
                                },
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    {/* 相关文章列表 */}
                    {recommendTags?.length > 0 && (
                      <div className={styles.relatedArticles}>
                        <h2 className={styles.relatedArticlesTitle}>
                          <span className={styles.titleIndicator} />
                          {t('Related Articles')}
                        </h2>
                        <div className={styles.relatedArticlesList}>
                          {(recommendTags ?? []).map((item: HomeArticleModel, index: number) => {
                            return (
                              <div className={styles.relatedArticleItem} key={item?.article_id}>
                                <span className={styles.relatedArticleNumber}>{index + 1}</span>
                                <Link className={styles.relatedArticleLink} href={`/blog/${item?.title_id}`}>
                                  {item.title}
                                </Link>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  {/* 推荐文章区域 - 卡片外 */}
                  {recommandArtcles?.length > 0 && (
                    <div className={styles.recommendArticles}>
                      <div className={styles.recommendArticlesTitle}>
                        <span className={styles.titleIndicatorSecondary} />
                        {t('peopleAlsoEnjoyed')}
                      </div>
                      <div className={styles.recommendArticlesList}>
                        <ArticleListContent articleList={recommandArtcles.slice(0, 6)} />
                      </div>
                    </div>
                  )}

                  <div className={styles.mobileSharePage}>
                    <SharePage shareUrl={currentHref} />
                  </div>
                </>
              )}
            </div>
            <div className={styles.otherContainer}>
              {articleList != undefined && articleList?.length > 1 && (
                <>
                  <h3 className={styles.otherContainerTitle}>{t('peopleAlsoEnjoyed')}</h3>
                  {(articleList ?? []).map((item, index) => {
                    return (
                      <HomeFullItem
                        key={index}
                        showBottomLine={index != articleList.length - 1}
                        articleModel={item}
                        showPadding={false}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>

        {isAddAccountModal && (
          <AddAccountModal
            ref={AccountModalRef}
            isModalOpen={isAddAccountModal}
            handleCancel={() => setIsAddAccountModal(false)}
            isHome={true}
            pageSource={EventNameSource.blogFeature}
          ></AddAccountModal>
        )}
        {isManualScroll && model?.article_button && <BottomFlexButton model={model?.article_button} />}
      </CommonBaseContent>
      {!model?.article_button ? (
        <div className={styles.bottomBtn}>
          <Button onClick={() => setIsAddAccountModal(true)}>Get Started Free</Button>
        </div>
      ) : (
        <div></div>
      )}
    </>
  );
};

export default ArticleDetailUI;
